/**
 * The end-of-study survey, drawn from the questions the server serves
 * (`study.json`). Submit stays disabled until every required answer is in —
 * by the same rule the server stores them with (`cleanSurveyAnswers`).
 */

import { useMemo, useState } from 'react'
import type { StudySurveyAnswers, StudySurveyQuestion } from '@overcooked/shared'
import { cleanSurveyAnswers } from '@overcooked/shared'

interface SurveyProps {
  questions: StudySurveyQuestion[]
  partnerLeft: boolean
  /** `submitting` once sent; `error` when the server refused or never answered. */
  status: 'idle' | 'submitting' | 'error'
  error: string | null
  onSubmit(answers: StudySurveyAnswers): void
}

export function Survey({ questions, partnerLeft, status, error, onSubmit }: SurveyProps) {
  const [answers, setAnswers] = useState<Record<string, string | number>>({})
  const set = (id: string, value: string | number): void => setAnswers((prev) => ({ ...prev, [id]: value }))
  const cleaned = useMemo(() => cleanSurveyAnswers(questions, answers), [questions, answers])

  if (status === 'submitting') {
    return (
      <section className="gate" role="status">
        <h1>Saving your answers…</h1>
        <p className="hint">Keep this tab open.</p>
        <div className="study-spinner" aria-hidden="true" />
      </section>
    )
  }

  return (
    <section className="gate study-survey">
      <p className="gate-step">Last step</p>
      <h1>A few questions</h1>
      {partnerLeft && (
        <p className="notice">Your partner left early. Answer about the rounds you played.</p>
      )}
      <p className="hint">Questions marked * are required.</p>
      <ol className="study-questions">
        {questions.map((q) => (
          <li key={q.id} className="study-question">
            <p className="study-question-text">
              {q.text}
              {q.required && <span aria-hidden="true"> *</span>}
            </p>
            {q.type === 'likert' && (
              <div className="study-likert" role="radiogroup" aria-label={q.text}>
                <span className="hint">{q.scale.minLabel}</span>
                {Array.from({ length: q.scale.max - q.scale.min + 1 }, (_, i) => q.scale.min + i).map((value) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={answers[q.id] === value}
                    className={answers[q.id] === value ? 'study-choice study-choice-on' : 'study-choice'}
                    onClick={() => set(q.id, value)}
                  >
                    {value}
                  </button>
                ))}
                <span className="hint">{q.scale.maxLabel}</span>
              </div>
            )}
            {q.type === 'multiple_choice' && (
              <div className="study-options" role="radiogroup" aria-label={q.text}>
                {q.options.map((option) => (
                  <label key={option} className="consent-check">
                    <input type="radio" name={q.id} checked={answers[q.id] === option} onChange={() => set(q.id, option)} />
                    {option}
                  </label>
                ))}
                {q.otherOption && answers[q.id] === q.otherOption && (
                  <input
                    className="study-text"
                    placeholder="Please describe"
                    maxLength={500}
                    value={String(answers[`${q.id}_other`] ?? '')}
                    onChange={(event) => set(`${q.id}_other`, event.target.value)}
                  />
                )}
              </div>
            )}
            {q.type === 'text' && (
              <textarea
                className="study-text"
                rows={3}
                maxLength={q.maxLength ?? 2000}
                value={String(answers[q.id] ?? '')}
                onChange={(event) => set(q.id, event.target.value)}
              />
            )}
          </li>
        ))}
      </ol>
      {error && <p className="error">{error}</p>}
      <div className="gate-actions">
        <button
          type="button"
          className="consent-agree"
          disabled={!cleaned}
          onClick={() => cleaned && onSubmit(cleaned)}
          data-testid="survey-submit"
        >
          Submit
        </button>
      </div>
      {!cleaned && <p className="hint">Answer every required question to submit.</p>}
    </section>
  )
}
